import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { ImageIntentZoomContentComponent } from './imageIntentZoomContent';
import {
  selectImageProtectionEnabled,
  selectImageProtectionStyle,
} from '../../../../selectors/imageIntent';
import { overlayBackgroundFor, usesSplitBlur } from './overlayStyles';
import type { OverlayStyle } from './overlayStyles';
import SplitBlurOverlay from './SplitBlurOverlay';

// react-medium-image-zoom renders the fullscreen image as a *clone* in a
// portalled <dialog>, so the vignette we render as a sibling of the thumbnail
// does not travel with it — the zoomed view was the one surface that stayed
// unprotected. rmiz's ZoomContent render prop hands us the modal <img> element
// it is about to render, with its computed geometry on `props.style`; we mirror
// that geometry onto an overlay div drawn on top of it.
//
// Deterrence only, same as the thumbnail overlay: the top half stays clear.

type ZoomContentArgs = Parameters<ImageIntentZoomContentComponent>[0];

// rmiz lays the modal image out with inline top/left/width/height and then
// animates it into place with `transform` (its own stylesheet supplies
// `transform-origin: top left` and `transition: transform 0.3s`). Copying the
// box alone puts the overlay in the pre-animation position, so we mirror the
// transform and re-declare the origin/transition that rmiz's stylesheet only
// applies to [data-rmiz-modal-img].
//
// A missing box means rmiz has not measured yet (LOADING/UNLOADED) — skip the
// overlay for that frame rather than render a mispositioned block. Returns
// just the mirrored geometry; callers add their own `background` (the flat
// CSS overlay) or wrap a `SplitBlurOverlay` inside it (split blur).
const geometryStyleFor = (
  img: React.ReactElement | null,
): React.CSSProperties | null => {
  const imgStyle = (img?.props as { style?: React.CSSProperties })?.style;
  if (!imgStyle) return null;

  const { top, left, width, height, transform } = imgStyle;
  if ([top, left, width, height].some((v) => v === undefined || v === null)) {
    return null;
  }

  return {
    position: 'absolute',
    top,
    left,
    width,
    height,
    transform,
    transformOrigin: 'top left',
    transition: 'transform 0.3s',
    pointerEvents: 'none',
    zIndex: 1,
  };
};

// The overlay for rmiz's modal image, or null until rmiz has measured it.
const zoomOverlayFor = (
  img: React.ReactElement | null,
  protectionStyle: OverlayStyle,
): React.ReactElement | null => {
  const geometry = geometryStyleFor(img);
  if (!geometry) return null;

  const modalSrc = (img?.props as { src?: string } | undefined)?.src;

  if (usesSplitBlur(protectionStyle) && modalSrc) {
    // The blurred layer and its toggle button live inside SplitBlurOverlay,
    // so this wrapper only supplies the mirrored geometry — no aria-hidden
    // (the toggle is interactive). Pointer events stay `none` (from
    // `geometry`, same as the flat wash) so a click on the image still
    // reaches rmiz's click-to-unzoom handler; SplitBlurOverlay's own toggle
    // button opts itself back into pointer events, so it stays clickable
    // without this wrapper needing to.
    return (
      <div
        data-testid="image-protection-overlay-zoom"
        data-overlay-style={protectionStyle}
        style={geometry}
      >
        <SplitBlurOverlay />
      </div>
    );
  }

  // Split blur with no modal <img> src yet (or any other style) falls back to
  // the flat wash, so the zoom is never left unprotected.
  const style = usesSplitBlur(protectionStyle) ? 'scrim' : protectionStyle;
  return (
    <div
      aria-hidden="true"
      data-testid="image-protection-overlay-zoom"
      data-overlay-style={style}
      style={{ ...geometry, background: overlayBackgroundFor(style) }}
    />
  );
};

// Use as: <ControlledZoom ZoomContent={useProtectedZoomContent(inner)}>
//
// `inner` is the zoom content the surface would render without protection —
// on the pose picker and product config, useImageIntentZoomContent's, which
// puts the save-intent handlers on the modal image (ENG-2405). Call both
// hooks unconditionally and pass one to the other: with protection on, the
// result renders the inner content (the modal image with its handlers) with
// the overlay drawn over the image and under the unzoom button; with
// protection off it is `inner` itself, so rmiz renders exactly what it did
// before. Without an `inner`, protection off returns undefined (rmiz's own
// default content) and protection on renders the plain modal image.
//
// The component's identity changes only when the protection settings or
// `inner` do: a new identity remounts the modal image.
const useProtectedZoomContent = (
  inner?: ImageIntentZoomContentComponent,
): ImageIntentZoomContentComponent | undefined => {
  const protectionEnabled = useSelector(selectImageProtectionEnabled);
  const protectionStyle = useSelector(selectImageProtectionStyle);

  return useMemo(() => {
    if (!protectionEnabled) return inner;

    const ProtectedZoomContent: ImageIntentZoomContentComponent = (
      props: ZoomContentArgs,
    ) => {
      const { img, buttonUnzoom } = props;
      const overlay = zoomOverlayFor(img, protectionStyle);

      if (inner) {
        const Inner = inner;
        // The inner content renders the modal image, then `buttonUnzoom`;
        // handing it the overlay ahead of the button puts the overlay
        // between the two, as rmiz's own layering expects.
        return (
          <Inner
            {...props}
            buttonUnzoom={
              <>
                {overlay}
                {buttonUnzoom}
              </>
            }
          />
        );
      }

      return (
        <>
          {img}
          {overlay}
          {buttonUnzoom}
        </>
      );
    };

    return ProtectedZoomContent;
  }, [protectionEnabled, protectionStyle, inner]);
};

export default useProtectedZoomContent;
